"use client";

import Image from "next/image";
import Link from "next/link";

interface ServiceCardProps {
  title: string;
  excerpt: string;
  number: string;
  image: string;
  href?: string;
}

export default function ServiceCard({
  title,
  excerpt,
  number,
  image,
  href = "#",
}: ServiceCardProps) {
  return (
    <Link
      href={href}
      className="group flex flex-col w-full bg-white rounded-[10px] border border-neutral-200 shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] hover:shadow-[0_40px_80px_0_rgba(0,0,0,0.12)] relative overflow-hidden transition-all duration-300"
    >
      {/* Image Container */}
      <div className="relative h-48 sm:h-56 lg:h-[260px] rounded-t-[10px] overflow-hidden bg-gray-100">
        {image && (
          <Image
            src={image || ""}
            alt={title}
            fill
            draggable={false}
            className="object-cover transform group-hover:scale-105 transition-transform duration-500"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            loading="lazy"
            onError={(e) => {
              // Fallback to a placeholder if image fails to load
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        {/* Number Badge */}
        <div className="absolute top-3 sm:top-[17px] right-3 sm:right-[17px] w-8 sm:w-[40px] h-8 sm:h-[40px] bg-white rounded-full shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] group-hover:bg-primary-800 group-hover:text-white transition-colors duration-300 flex items-center justify-center">
          <span className="text-dark-200 group-hover:text-white text-sm sm:text-[17px] font-medium leading-[16px] transition-colors duration-300">
            {parseInt(number) > 9 ? number : `0${number}`}
          </span>
        </div>
      </div>

      {/* Content Container */}
      <div className="flex flex-col p-4 sm:p-6 lg:p-7 gap-3 sm:gap-4 lg:gap-[18px] lg:min-h-[218px] min-h-[159px]">
        <h3 className="text-dark-200 text-lg sm:text-xl lg:text-[24px] font-medium leading-tight lg:leading-[34px] tracking-tight lg:tracking-[-0.72px] group-hover:text-primary-800 transition-colors duration-300 h-11 md:h-[50px] lg:h-17 line-clamp-2">
          {title}
        </h3>
        <div className="w-full h-[1px] sm:h-[2px] bg-neutral-200 relative overflow-hidden">
          <div className="absolute left-0 top-0 w-0 h-full bg-primary-800 transition-all duration-500 ease-out group-hover:w-full"></div>
        </div>
        <p className="text-dark-200 text-sm sm:text-base lg:text-[18px] font-normal leading-relaxed lg:leading-[28px] line-clamp-2 overflow-hidden">
          {excerpt}
        </p>
      </div>
    </Link>
  );
}
